import React, { useEffect, useMemo, useState, useCallback } from 'react';
import AppShell from '@/components/layout/AppShell';
import { ownerNav } from '@/lib/navConfig';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Building2, Zap, Droplets, RotateCcw, Wifi, HandCoins } from 'lucide-react';
import api, { fmtIDR, fmtDate, currentMonth } from '@/lib/api';
import { toast } from 'sonner';

// Predefined sub-categories with quick-select. User can type custom.
const SUB_CATEGORIES = [
  { label: 'Listrik', icon: Zap },
  { label: 'Air', icon: Droplets },
  { label: 'Cashback', icon: RotateCcw },
  { label: 'Internet', icon: Wifi },
  { label: 'Sewa', icon: Building2 },
  { label: 'Pajak', icon: HandCoins },
];

export default function OwnerCompanyExpenses() {
  const [name, setName] = useState('');
  const [subCat, setSubCat] = useState('');
  const [amount, setAmount] = useState('');
  const [description, setDescription] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [history, setHistory] = useState([]);
  const [month, setMonth] = useState(currentMonth());

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/cash/movements?kind=out&month=${month}&category=beban_perusahaan`);
      setHistory(data);
    } catch (e) {
      console.error('[OwnerCompanyExpenses] gagal load:', e);
      toast.error(e?.response?.data?.detail || 'Gagal memuat');
    }
  }, [month]);
  useEffect(() => { load(); }, [load]);

  const totalMonth = useMemo(() => history.reduce((a, h) => a + (h.amount || 0), 0), [history]);
  const grouped = useMemo(() => {
    const map = {};
    history.forEach((h) => {
      const key = (h.sub_category || 'Lainnya').trim();
      if (!map[key]) map[key] = { count: 0, total: 0 };
      map[key].count += 1;
      map[key].total += h.amount;
    });
    return Object.entries(map).map(([k, v]) => ({ subCat: k, ...v })).sort((a, b) => b.total - a.total);
  }, [history]);

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim()) return toast.error('Nama pengeluaran wajib diisi');
    if (!subCat.trim()) return toast.error('Kategori beban wajib diisi (mis. Listrik / Air / Cashback)');
    const n = Number(amount);
    if (!n || n <= 0) return toast.error('Nominal tidak valid');
    setSubmitting(true);
    try {
      const { data } = await api.post('/cash/expenses', {
        category: 'beban_perusahaan',
        recipient_name: name.trim(),
        sub_category: subCat.trim(),
        amount: n,
        description: description.trim() || null,
      });
      toast.success(`Beban tercatat. Saldo kas: ${fmtIDR(data.balance)}`);
      setName(''); setSubCat(''); setAmount(''); setDescription('');
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Gagal simpan'); }
    finally { setSubmitting(false); }
  };

  return (
    <AppShell nav={ownerNav} title="Beban Perusahaan" showBell>
      <div className="space-y-4">
        <div className="rounded-lg border bg-violet-50 p-3 text-xs text-violet-900">
          <b>Info:</b> Beban perusahaan (Listrik, Air, Cashback, Sewa, Pajak, dll) otomatis tercatat di modul <b>Pengeluaran</b> (kategori Beban Perusahaan) dan mengurangi <b>Laba Bersih</b> di Laporan.
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* Form */}
          <Card>
            <CardHeader><CardTitle className="text-base flex items-center gap-2"><Building2 className="h-4 w-4 text-violet-600" />Input Beban Perusahaan</CardTitle></CardHeader>
            <CardContent>
              <form onSubmit={submit} className="space-y-3">
                <div className="space-y-1.5">
                  <Label>Nama Pengeluaran Beban <span className="text-rose-500">*</span></Label>
                  <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="mis. PLN Bulan Juli, PDAM, Cashback Customer X" data-testid="beban-name" />
                </div>
                <div className="space-y-1.5">
                  <Label>Kategori Beban <span className="text-rose-500">*</span></Label>
                  <Input value={subCat} onChange={(e) => setSubCat(e.target.value)} placeholder="mis. Listrik / Air / Cashback / Internet / Sewa / dll" data-testid="beban-subcat" />
                  <div className="flex flex-wrap gap-1.5 pt-1">
                    {SUB_CATEGORIES.map((s) => (
                      <button type="button" key={s.label} onClick={() => setSubCat(s.label)} className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-[10px] hover:bg-accent transition-colors ${subCat === s.label ? 'bg-violet-100 border-violet-300 text-violet-700' : ''}`} data-testid={`beban-quick-${s.label}`}>
                        <s.icon className="h-3 w-3" />{s.label}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="space-y-1.5">
                  <Label>Besar Beban (Rupiah) <span className="text-rose-500">*</span></Label>
                  <Input type="number" min="1" step="1" inputMode="numeric" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="mis. 1500000" data-testid="beban-amount" />
                </div>
                <div className="space-y-1.5">
                  <Label>Deskripsi (opsional)</Label>
                  <Textarea rows={2} value={description} onChange={(e) => setDescription(e.target.value)} data-testid="beban-description" />
                </div>
                <Button type="submit" disabled={submitting} className="w-full bg-violet-600 hover:bg-violet-700" data-testid="beban-submit">
                  {submitting ? 'Menyimpan...' : 'Catat Beban'}
                </Button>
              </form>
            </CardContent>
          </Card>

          {/* Ringkasan */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Ringkasan Bulan Ini</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex items-end justify-between">
                <div>
                  <div className="text-xs text-muted-foreground">Total Beban Bulan Ini</div>
                  <div className="text-2xl font-heading font-semibold tabular-nums text-violet-700" data-testid="beban-total">{fmtIDR(totalMonth)}</div>
                </div>
                <Input type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="w-40" data-testid="beban-month" />
              </div>
              {grouped.length === 0 ? (
                <div className="text-center py-6 text-xs text-muted-foreground">Belum ada beban bulan ini.</div>
              ) : (
                <div className="space-y-1.5">
                  {grouped.map((g) => (
                    <div key={g.subCat} className="flex items-center justify-between rounded-lg border bg-violet-50/60 p-2">
                      <div className="text-sm">
                        <b>{g.subCat}</b>
                        <span className="text-[10px] text-muted-foreground ml-2">{g.count} kali</span>
                      </div>
                      <div className="text-sm tabular-nums font-semibold">{fmtIDR(g.total)}</div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* History detail */}
        <Card>
          <CardHeader className="pb-3"><CardTitle className="text-base">Riwayat Beban ({history.length})</CardTitle></CardHeader>
          <CardContent className="p-0">
            {history.length === 0 ? (
              <div className="text-center py-8 text-sm text-muted-foreground">Belum ada beban tercatat bulan ini.</div>
            ) : (
              <div className="overflow-x-auto">
                <Table data-testid="beban-history">
                  <TableHeader>
                    <TableRow>
                      <TableHead>Tanggal</TableHead>
                      <TableHead>Nama</TableHead>
                      <TableHead>Kategori</TableHead>
                      <TableHead>Deskripsi</TableHead>
                      <TableHead className="text-right">Nominal</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {history.map((h) => (
                      <TableRow key={h.id} data-testid={`beban-history-${h.id}`}>
                        <TableCell className="text-xs text-muted-foreground whitespace-nowrap">{fmtDate(h.created_at)}</TableCell>
                        <TableCell className="font-medium">{h.recipient_name}</TableCell>
                        <TableCell><Badge className="bg-violet-100 text-violet-700 hover:bg-violet-100 border-0">{h.sub_category || 'Lainnya'}</Badge></TableCell>
                        <TableCell className="text-xs text-muted-foreground">{h.description || '-'}</TableCell>
                        <TableCell className="text-right tabular-nums font-semibold text-violet-700">-{fmtIDR(h.amount)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </AppShell>
  );
}
